import { AttributeFormatDc, AttributeType, StringSubType, GeometryType } from "@evergis/api";
import { TreeItemProps, UploaderItemProps } from "@evergis/ui";

import { AttributesUpdate, UpdateAttribute } from "hooks/attributes/useAttributes";
import { ImportDataSchema } from "@evergis/api/dist/__generated__/data-contracts";
import { ReactNode } from "react";
import { AttributeError } from "../../../utils/validateAttributeName";
import {
  SpatialReferenceActions,
  SpatialReferenceData,
  SrFields,
  SrFieldUpdateAction,
} from "./hooks/useSpatialReference";
import { SchemaParsed } from "../../../evergis/core/import/parseSchema";
import { FileData, FileLoader } from "./hooks/useFileLoader";
import { ResourceMeta, ResourceMetaUpdate } from "../../../hooks/useResourceMeta";

export interface ImportProgressBlockProps {
  importInProgress?: boolean;
  layerCreating?: boolean;
  layerCreated?: boolean;
  estimatedRecords?: number;
  processedRecords?: number;
}

export interface ImportError {
  message?: string;
  count?: number;
  log?: string;
}

export interface ImportErrorsProps {
  error: ImportError;
  importInProgress: boolean;
}

export type ImportAttribute = {
  name: string;
  originalName?: string;
  type: AttributeType;
  subType?: StringSubType;
  alias?: string;
  id?: string;
  disabled?: boolean;
  expression?: string;
  idAttribute?: string;
  stringFormat?: AttributeFormatDc;
  sysAttribute?: boolean;
  attributeName?: string;
  columnName?: string;
};

export interface ImportAttributeProps {
  attribute: ImportAttribute;
  updateAttribute?: AttributesUpdate<ImportAttribute>["updateAttribute"];
  idField?: string;
  idAttribute?: string;
  geometryAttribute?: string;
  error: Nullable<AttributeError>;
  firstRowValue?: string;
  onDeleteSysAttribute?: (id: string) => void;
  onChangeSysAttribute?: UpdateAttribute<ImportAttribute>;
}

export interface ImportState {
  importInProgress: boolean;
  processed: number;
  importDone: boolean;
  taskId: string;
}

export interface ImportErrorState {
  message: string;
  count: number;
  log: string;
}

export type AttributeOption = {
  text: ImportAttribute["name"];
  value: ImportAttribute["name"];
};

export interface SpatialReferenceBlockProps extends SpatialReferenceData, SpatialReferenceActions {
  attributes?: ImportAttribute[];
}

export type ImportObject = TreeItemProps & {
  type?: GeometryType;
};

export enum ImportType {
  XLSX,
  SHAPE,
  KML,
  GDB,
}

export type DataSchema = SchemaParsed | SchemaParsed[] | ImportDataSchema;

export interface ImportContextType {
  step?: ImportStep;
  importType?: ImportType;
  nextStep?: VoidFunction;
  prevStep?: VoidFunction;
  target?: ImportTargetType;
  setTarget?: (target: ImportTargetType) => void;
  file?: FileData & FileLoader;
  fileType?: string;
  setImportType?: (type: ImportType) => void;
  setFileType?: (fileType: string) => void;
  setFile?: (file: FileData & FileLoader) => void;
  importedFiles?: UploaderItemProps[];
  setImportedFiles?: (importedFiles: UploaderItemProps[]) => void;
  dataSchema?: DataSchema;
  setDataSchema?: (dataSchema: DataSchema) => void;
  objects?: ImportObject[];
  setObjects?: (objects: ImportObject[]) => void;
  attributes?: ImportAttribute[];
  setAttributes?: (attributes: ImportAttribute[]) => void;
  sysAttributes?: ImportAttribute[];
  addSysAttributes?: (attributes: ImportAttribute[]) => void;
  deleteSysAttribute?: (id: string) => void;
  updateSysAttribute?: UpdateAttribute<ImportAttribute>;
  meta?: ResourceMeta;
  updateMeta?: ResourceMetaUpdate;
  srFields?: SrFields;
  lonLatInOneField?: boolean;
  geometrySpecified?: boolean;
  updateSrField?: SrFieldUpdateAction;
  setLonLatInOneField?: (isInOneField: SpatialReferenceData["lonLatInOneField"]) => void;
  geometryType?: GeometryType;
  setGeometryType?: (geometryType: GeometryType) => void;
}

export enum ExcelImportStep {
  File,
  Coordinates,
  Attributes,
  Meta,
  Finish,
}

export enum KMLImportStep {
  File,
  Objects,
  Attributes,
  Meta,
  Finish,
}

export enum ShapeImportStep {
  File,
  Attributes,
  Meta,
  Finish,
}

export enum GDBImportFromPanelStep {
  File,
  Layers,
  Finish,
}

export enum GDBImportFromLayersStep {
  File,
  Layers,
  Target,
  Finish,
}

export enum GDBImportFromProjectsStep {
  File,
  Layers,
  Meta,
  Finish,
}

export type ImportStep =
  | ExcelImportStep
  | KMLImportStep
  | ShapeImportStep
  | GDBImportFromPanelStep
  | GDBImportFromLayersStep
  | GDBImportFromProjectsStep;

export enum ImportTargetType {
  Catalog,
  NewMap,
}
